// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever leaves the device — the AI (recap/reflect/ideas) is called separately
// in brain.ts with ONLY the derived-signal slice computed in Rust (signals.rs).
//
// Data model — a UNIVERSAL collection catalogue:
//   collection { name, kind, attr_schema (custom fields), notes }
//   item       { collection_id, name, photo_ref, acquired_on, source, cost (user value),
//                condition, attrs (keyed by the collection schema), wishlist, notes, set_id }
//   set        { collection_id, name, target_size }

import { invoke } from "./tauri-web";

// ── Custom attribute schema (the HEART of the app) ──────────────────────────────
// A collection defines its OWN small set of fields. Each item then carries values
// keyed by these field keys. This is what makes the catalogue universal: a rock
// collection has "mineral / hardness / locality"; a vinyl collection has
// "artist / year / pressing". The schema is stored as JSON on the collection.
export type AttrFieldKind = "text" | "number" | "select" | "date" | "bool";
export interface AttrField {
  /** Stable key the item's attrs object is keyed by. */
  key: string;
  /** Human label shown in the UI. */
  label: string;
  kind: AttrFieldKind;
  /** Options for a select field (ignored otherwise). */
  options?: string[];
}
/** An item's custom attribute values, keyed by AttrField.key. */
export type AttrValues = Record<string, string | number | boolean | null>;

export interface Collection {
  id: string;
  name: string;
  /** Collection type the user chose (rocks / vinyl / coins / scarves / …). */
  kind: string;
  /** Custom field definitions for this collection. */
  attr_schema: AttrField[];
  notes: string;
  created_at: string;
  updated_at: string;
}
export interface NewCollection {
  name: string;
  kind?: string;
  attr_schema?: AttrField[];
  notes?: string;
}
export type CollectionPatch = Partial<{
  name: string;
  kind: string;
  attr_schema: AttrField[];
  notes: string;
}>;

export interface Item {
  id: string;
  collection_id: string;
  name: string;
  /** Local file path / asset URI the user picked. Never uploaded. */
  photo_ref: string;
  /** "YYYY-MM-DD" or "" if unknown. */
  acquired_on: string;
  source: string;
  /** Value the USER entered — never a market appraisal. null = not set. */
  cost: number | null;
  condition: string;
  attrs: AttrValues;
  /** have (false) vs want / wishlist (true). */
  wishlist: boolean;
  notes: string;
  /** Optional set membership ("" = ungrouped). */
  set_id: string;
  created_at: string;
  updated_at: string;
}
export interface NewItem {
  collection_id: string;
  name: string;
  photo_ref?: string;
  acquired_on?: string;
  source?: string;
  cost?: number | null;
  condition?: string;
  attrs?: AttrValues;
  wishlist?: boolean;
  notes?: string;
  set_id?: string;
}
export type ItemPatch = Partial<{
  name: string;
  photo_ref: string;
  acquired_on: string;
  source: string;
  // double-Option on the Rust side: pass `{ cost: null }` to clear it.
  cost: number | null;
  condition: string;
  attrs: AttrValues;
  wishlist: boolean;
  notes: string;
  set_id: string;
  /** Move the item to another collection (sets are per-collection, so a move clears set_id). */
  collection_id: string;
}>;

export interface CollectionSet {
  id: string;
  collection_id: string;
  name: string;
  target_size: number | null;
  notes: string;
  created_at: string;
  updated_at: string;
}
export interface NewSet {
  collection_id: string;
  name: string;
  target_size?: number | null;
  notes?: string;
}
export type SetPatch = Partial<{
  name: string;
  target_size: number | null;
  notes: string;
}>;

export interface ProvenanceEvent {
  id: string;
  item_id: string;
  occurred_on: string;
  kind: string;
  text: string;
  created_at: string;
}
export interface NewProvenanceEvent {
  item_id: string;
  occurred_on?: string;
  kind?: string;
  text: string;
}

// ── Derived signals (computed on-device in Rust; only this slice is sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::CollectionSignals. Every field is a computed aggregate
// or one of the user's own labels — counts, sums, set completeness, attribute breakdowns,
// acquisition cadence, duplicates, value extremes (USER-ENTERED only), recent adds, wishlist.
// This is the payload the AI reasons over (see brain.ts), so the model narrates SPECIFIC
// facts about the user's own catalogue instead of re-reading the rows.
export interface Counts {
  total: number;
  have: number;
  want: number;
  in_sets: number;
  ungrouped: number;
}
export interface ValueStats {
  items_with_value: number;
  total_logged_value: number;
  avg_logged_value: number | null;
  min_logged_value: number | null;
  max_logged_value: number | null;
  note: string;
}
export interface SetCompleteness {
  name: string;
  target_size: number | null;
  have: number;
  want: number;
  remaining: number | null;
  percent_complete: number | null;
  one_from_complete: boolean;
  complete: boolean;
}
export interface LabelCount {
  label: string;
  count: number;
}
export interface AttributeBreakdown {
  key: string;
  label: string;
  filled: number;
  values: LabelCount[];
  distinct_values: number;
}
export interface YearStat {
  year: string;
  count: number;
}
export interface CadenceShift {
  this_year: number;
  last_year: number;
  delta: number;
  direction: "more" | "fewer" | "same";
  busiest_year: string;
  busiest_year_count: number;
}
export interface DuplicateGroup {
  name: string;
  count: number;
}
export interface ValuedItem {
  name: string;
  logged_value: number;
  set_name: string | null;
}
export interface RecentItem {
  name: string;
  acquired_on: string | null;
  logged_value: number | null;
}
export interface WishlistSignalItem {
  name: string;
  set_name: string | null;
}
export interface SetCompletenessLite {
  name: string;
  have: number;
  target_size: number;
  remaining: number;
}
export interface SetSummary {
  with_target: number;
  complete: number;
  one_from_complete: number;
  avg_percent_complete: number | null;
  closest_open: SetCompletenessLite | null;
}
export interface CollectionFocus {
  field_label: string;
  value_label: string;
  count: number;
  share_pct: number;
}
/** Sentiment of a fact FOR THE COLLECTOR — UI tinting only, never a recommendation. */
export type FactTone = "good" | "watch" | "neutral";
/** A pre-ranked, ready-to-say observational fact computed in Rust (signals.rs). */
export interface HeadlineFact {
  kind: string;
  text: string;
  tone: FactTone;
}
export interface CollectionSignals {
  schema: string;
  generated_for: string;
  collection_name: string;
  collection_kind: string;
  counts: Counts;
  value: ValueStats;
  sets: SetCompleteness[];
  set_summary: SetSummary;
  attribute_breakdown: AttributeBreakdown[];
  focus: CollectionFocus | null;
  conditions: LabelCount[];
  sources: LabelCount[];
  acquisition_by_year: YearStat[];
  acquisition_cadence: CadenceShift | null;
  duplicates: DuplicateGroup[];
  most_valued: ValuedItem[];
  least_valued: ValuedItem[];
  recent_additions: RecentItem[];
  wishlist: WishlistSignalItem[];
  headline_facts: HeadlineFact[];
}

export const store = {
  // Collections
  listCollections: () => invoke<Collection[]>("list_collections"),
  getCollection: (id: string) => invoke<Collection | null>("get_collection", { id }),
  createCollection: (input: NewCollection) => invoke<Collection>("create_collection", { input }),
  updateCollection: (id: string, patch: CollectionPatch) => invoke<void>("update_collection", { id, patch }),
  deleteCollection: (id: string) => invoke<void>("delete_collection", { id }),

  // Sets
  listSets: (collectionId: string) => invoke<CollectionSet[]>("list_sets", { collectionId }),
  createSet: (input: NewSet) => invoke<CollectionSet>("create_set", { input }),
  updateSet: (id: string, patch: SetPatch) => invoke<void>("update_set", { id, patch }),
  deleteSet: (id: string) => invoke<void>("delete_set", { id }),

  // Provenance events (per item)
  listProvenanceEvents: (itemId: string) => invoke<ProvenanceEvent[]>("list_provenance_events", { itemId }),
  createProvenanceEvent: (input: NewProvenanceEvent) => invoke<ProvenanceEvent>("create_provenance_event", { input }),
  deleteProvenanceEvent: (id: string) => invoke<void>("delete_provenance_event", { id }),

  // Items
  listItems: (collectionId?: string, limit?: number) =>
    invoke<Item[]>("list_items", { collectionId, limit: limit ?? 1_000_000 }),
  getItem: (id: string) => invoke<Item | null>("get_item", { id }),
  createItem: (input: NewItem) => invoke<Item>("create_item", { input }),
  updateItem: (id: string, patch: ItemPatch) => invoke<void>("update_item", { id, patch }),
  deleteItem: (id: string) => invoke<void>("delete_item", { id }),

  /** Compute the derived-signal slice for ONE collection on-device. `today` = client local YYYY-MM-DD. */
  computeSignals: (collectionId: string, today: string) =>
    invoke<CollectionSignals>("compute_signals", { collectionId, today }),
};

// ── Pure helpers ────────────────────────────────────────────────────────────────

/** Starter collection types — quick picks; the user can also type their own. */
export const COLLECTION_KIND_PRESETS = [
  "Rocks & minerals", "Vinyl records", "Coins", "Trading cards", "Stamps",
  "Vases", "Scarves", "Figurines", "Art", "Books", "Watches", "Other",
] as const;

/** Common condition labels — suggestions, not enforced. */
export const CONDITION_PRESETS = [
  "Mint", "Near mint", "Excellent", "Good", "Fair", "Poor", "Restored",
] as const;

/**
 * Curated starter custom-field schemas per collection kind. Picking a preset kind
 * seeds these fields so a non-savvy user gets a real schema without designing one;
 * they can edit/add/remove freely. This is the universal-catalogue payoff made easy.
 */
export const SCHEMA_PRESETS: Record<string, AttrField[]> = {
  "Rocks & minerals": [
    { key: "mineral", label: "Mineral / type", kind: "text" },
    { key: "locality", label: "Locality / origin", kind: "text" },
    { key: "color", label: "Colour", kind: "text" },
    { key: "size_mm", label: "Size (mm)", kind: "number" },
  ],
  "Vinyl records": [
    { key: "artist", label: "Artist", kind: "text" },
    { key: "year", label: "Year", kind: "number" },
    { key: "pressing", label: "Pressing", kind: "text" },
    { key: "genre", label: "Genre", kind: "text" },
  ],
  Coins: [
    { key: "country", label: "Country", kind: "text" },
    { key: "year", label: "Year", kind: "number" },
    { key: "denomination", label: "Denomination", kind: "text" },
    { key: "metal", label: "Metal", kind: "text" },
  ],
  "Trading cards": [
    { key: "set", label: "Set", kind: "text" },
    { key: "number", label: "Card number", kind: "text" },
    { key: "rarity", label: "Rarity", kind: "select", options: ["Common", "Uncommon", "Rare", "Ultra rare"] },
    { key: "graded", label: "Graded", kind: "bool" },
  ],
  Stamps: [
    { key: "country", label: "Country", kind: "text" },
    { key: "year", label: "Year", kind: "number" },
    { key: "theme", label: "Theme", kind: "text" },
  ],
  Vases: [
    { key: "material", label: "Material", kind: "text" },
    { key: "maker", label: "Maker / origin", kind: "text" },
    { key: "height_cm", label: "Height (cm)", kind: "number" },
  ],
  Scarves: [
    { key: "material", label: "Material", kind: "text" },
    { key: "brand", label: "Brand", kind: "text" },
    { key: "pattern", label: "Pattern", kind: "text" },
  ],
  Figurines: [
    { key: "series", label: "Series", kind: "text" },
    { key: "maker", label: "Maker", kind: "text" },
    { key: "material", label: "Material", kind: "text" },
  ],
  Art: [
    { key: "artist", label: "Artist", kind: "text" },
    { key: "medium", label: "Medium", kind: "text" },
    { key: "year", label: "Year", kind: "number" },
  ],
  Books: [
    { key: "author", label: "Author", kind: "text" },
    { key: "year", label: "Year", kind: "number" },
    { key: "edition", label: "Edition", kind: "text" },
    { key: "signed", label: "Signed", kind: "bool" },
  ],
  Watches: [
    { key: "brand", label: "Brand", kind: "text" },
    { key: "model", label: "Model", kind: "text" },
    { key: "year", label: "Year", kind: "number" },
    { key: "movement", label: "Movement", kind: "text" },
  ],
};

/** Slugify a label into a stable attribute key (lowercase, alnum + underscores). */
export function keyFromLabel(label: string): string {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "field";
}

/** Ensure a schema has unique, non-empty keys (used when building from labels). */
export function dedupeKeys(fields: AttrField[]): AttrField[] {
  const seen = new Set<string>();
  return fields.map((f) => {
    let k = f.key || keyFromLabel(f.label);
    let n = 1;
    while (seen.has(k)) {
      n += 1;
      k = `${f.key || keyFromLabel(f.label)}_${n}`;
    }
    seen.add(k);
    return { ...f, key: k };
  });
}

export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Format a user-entered value with a leading symbol; never implies appraisal. */
export function formatValue(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(v);
}

/** Pretty-print an attribute value for display. */
export function formatAttr(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}
